import { Box, Skeleton } from '@chakra-ui/react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Auth from './pages/Auth';
import Dashboard from './pages/Dashboard';
import PageNotFound from './components/PageNotFound';
import { ProtectedRoute } from './components/ProtectedRoute';
import { useEffect, useState } from 'react';
import { useDomainConfig } from './hooks/useDomainConfig';

import LandingPage from './components/LandingPage/LandingPage';
import Home from './components/Dashboard/Home';
import AssessmentCard from './components/Dashboard/AssessmentDetailsPage/AssessmentDetailsPage';
import AssessmentDetailsPage from './components/Dashboard/AssessmentDetailsPage/AssessmentDetailsPage';
import AssessmentFeedback from './components/Dashboard/AssessmentFeedback/AssessmentFeedback';
import Leaderboard from './components/Dashboard/Leaderboard/Leaderboard';
import AssessTestIframe from './components/Dashboard/AssessmentDetailsPage/AssessTestIframe';
import OrgLandingPage from './components/LandingPage/OrgLandingPage';
import Homepage from './pages/V2/Auth/Homepage';

import DashboardV2 from './pages/V2/DashboardV2/DashboardV2';
import ResumePage from './pages/V2/DashboardV2/ResumePage';
import CreateResume from './components/V2/Dashboard/CreateResume';
import { LearningContent } from './components/V2/Dashboard/LearningContent';
// import AspireCareer from './components/V2/Homepage/Onboarding/AspireCareer';
import OpportunitiesCards from './components/V2/Dashboard/OpportunitiesCards';

import StreaksPage from './pages/V2/Streaks/StreaksPage';
import Drawer from './components/V2/Roadmap/Drawer';
import StreakBoard from './components/V2/Dashboard/StreakBoard';
import EliteProgramDrawer from './components/V2/Dashboard/EliteProgramDrawer';
import OldUserResume from './components/V2/Dashboard/OldUserResume';

import RoadmapPage from './pages/V2/Roadmap/RoadmapPage';
import CommonWrapper from './components/common/V2/CommonWrapper';
import BlockItemsPage from './pages/V2/BlockItems/BlockItemsPage';
import BadgesPage from './pages/V2/Badges/BadgesPage';
import ProfilePage from './components/V2/Profile/ProfilePage';
import AspireCareer from './components/V2/Homepage/Onboarding/AspireCareer';
import LecturePage from './pages/V2/Lecture/LecturePage';
import ResourcePage from './pages/V2/Resource/ResourcePage';
import SignInAuth from './pages/V2/Auth/SignInAuth';
import { useUser, useUserProfile } from './hooks/V2/useUser';
import { AutoLogin } from './components/Auth/AutoLogin';
import { CourseSyllabus } from './components/Dashboard/CourseSyllabus';
declare global {
	interface Window {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		dataLayer: any; //
	}
}
export const App = () => {
	const { data, isLoading, isError } = useDomainConfig({ uiHost: window.location.host });
	const { data: user, isLoading: isUserLoading } = useUser();
	const { data: userProfile, isLoading: userProfileLoading } = useUserProfile();
	// const { data: userData, isLoading: isUserLoading, isError: isUserError } = useUser();

	useEffect(() => {
		// if user is not present and user is not on "/" or /signin then redirect to "/"
		if (
			!user &&
			!isUserLoading &&
			window.location.pathname !== '/' &&
			window.location.pathname !== '/sign-up' &&
			window.location.pathname !== '/sign-in' &&
			window.location.pathname !== '/auto-login'
		) {
			window.location.href = '/';
		}
	}, [isUserLoading, user]);

	useEffect(() => {
		if (window && window.dataLayer) {
			window.dataLayer.push({ user_id: user?._id });
		}
	}, [user?._id]);

	useEffect(() => {
		if (
			!userProfileLoading &&
			userProfile &&
			userProfile.enrolments &&
			userProfile.enrolments.length <= 0 &&
			window.location.pathname !== '/onboarding' &&
			window.location.pathname !== '/'
		) {
			window.location.href = '/onboarding';
		}
	}, [userProfile, userProfileLoading]);

	if (isLoading) return <Skeleton h="100vh">Loading...</Skeleton>;
	if (isError) return <div>Error</div>;

	return (
		<Box>
			<Router>
				{/* All routes are nested inside it */}
				<Routes>
					<Route
						path={'/'}
						element={
							<CommonWrapper showBottomNav={false} showLeftNav={false}>
								<OrgLandingPage />
							</CommonWrapper>
						}
					/>
					<Route
						path={'/sign-up'}
						element={
							<CommonWrapper showBottomNav={false} showLeftNav={false}>
								<Homepage />
							</CommonWrapper>
						}
					/>
					<Route
						path={'/onboarding'}
						element={
							<CommonWrapper showBottomNav={false} showLeftNav={false}>
								<Homepage />
							</CommonWrapper>
						}
					/>
					<Route
						path={'/sign-in'}
						element={
							<CommonWrapper isPrivate={false} showBottomNav={false} showLeftNav={false}>
								<SignInAuth />
							</CommonWrapper>
						}
					/>
					<Route
						path={'/dashboard'}
						element={
							<CommonWrapper>
								<DashboardV2 />
							</CommonWrapper>
						}
					/>
					<Route
						path={'/dashboard/published-item/:publishedItemId'}
						element={
							<CommonWrapper>
								<RoadmapPage />
							</CommonWrapper>
						}
					/>
					<Route
						path={'/dashboard/published-item/:publishedItemId/block-items'}
						element={
							<CommonWrapper>
								<BlockItemsPage />
							</CommonWrapper>
						}
					/>
					<Route
						path={'/dashboard/published-item/:publishedItemId/block-items/lecture/:lectureId'}
						element={
							<CommonWrapper>
								<LecturePage />
							</CommonWrapper>
						}
					/>
					<Route
						path={'/dashboard/published-item/:publishedItemId/block-items/resource/:resourceId'}
						element={
							<CommonWrapper>
								<ResourcePage />
							</CommonWrapper>
						}
					/>
					<Route
						path={'/dashboard/course/:courseId/syllabus'}
						element={
							<CommonWrapper>
								<CourseSyllabus />
							</CommonWrapper>
						}
					/>

					{/* Roadmap Published Item Page for a course */}

					<Route
						path={'/streaks'}
						element={
							<CommonWrapper showBottomNav={false} showLeftNav={false}>
								<StreaksPage />
							</CommonWrapper>
						}
					/>
					<Route
						path={'/badges'}
						element={
							<CommonWrapper showBottomNav={false} showLeftNav={false}>
								<BadgesPage />
							</CommonWrapper>
						}
					/>
					<Route
						path={'/resume'}
						element={
							<CommonWrapper>
								<ResumePage />
							</CommonWrapper>
						}
					/>
					<Route
						path={'/apply'}
						element={
							<CommonWrapper>
								<OpportunitiesCards />
							</CommonWrapper>
						}
					/>
					<Route
						path={'/profile'}
						element={
							<CommonWrapper>
								<ProfilePage />
							</CommonWrapper>
						}
					/>

					{/* <Route path={'/drawer'} element={<EliteProgramDrawer />} /> */}

					{/* Auth routes  */}
					{/* {['/sign-in', '/sign-up', '/reset-password', '/forgot-password'].map((path, index) => (
						<Route path={path} element={<Auth />} key={index} />
					))} */}

					{/* <Route
						path="/dashboard"
						element={
							<ProtectedRoute user={userData}>
								<Dashboard>
									<Home />
								</Dashboard>
							</ProtectedRoute>
						}
					/> */}
					{/* <Route
						path="/dashboard/published-item/:pushlishedItemId/assessment"
						element={
							<ProtectedRoute user={userData}>
								<Dashboard>
									<AssessmentDetailsPage />
								</Dashboard>
							</ProtectedRoute>
						}
					/> */}
					<Route
						path="/dashboard/published-item/:publishedItemId/assessment/start"
						element={
							// <ProtectedRoute user={userData}>
							<AssessTestIframe />
							// </ProtectedRoute>
						}
					/>
					<Route
						path="/dashboard/block-item/:blockItemId/assessment/start"
						element={
							// <ProtectedRoute user={userData}>
							<AssessTestIframe />
							// </ProtectedRoute>
						}
					/>
					<Route
						path="/auto-login"
						element={
							// <CommonWrapper showBottomNav={false} showLeftNav={false} showNa>
							<AutoLogin />
							// </CommonWrapper>
						}
					/>

					{/* Feedback and Leaderboard */}
					{/* <Route
						path="/dashboard/published-item/:pushlishedItemId/assessment/leaderboard"
						element={
							<ProtectedRoute user={userData}>
								<Dashboard>
									<AssessmentFeedback />
								</Dashboard>
							</ProtectedRoute>
						}
					/> */}

					<Route path="*" element={<PageNotFound />} />
				</Routes>
			</Router>
		</Box>
	);
};
